import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { useUpdateEarningsMutation, useUpdateExtendStayEarningsMutation } from "../../services/bClientApi";
import { useRef, useState } from 'react';

type MigrationStatus = {
    lastUpdatedId: string | null;
    updated: number;
    aggregateUpdated: number;
    complete: boolean;
};

type MigrationName = 'earnings' | 'extendStay';
const MIGRATION_BATCH_DELAY_MS = 1000;

export default function MigrationsPage() {
    const [updateEarnings, { isLoading: isUpdatingEarnings }] = useUpdateEarningsMutation();
    const [updateExtendStayEarnings, { isLoading: isUpdatingExtendStayEarnings }] = useUpdateExtendStayEarningsMutation();
    const [lastUpdatedEarnings, setLastUpdateEarnings] = useState<MigrationStatus>({ lastUpdatedId: null, updated: 0, aggregateUpdated: 0, complete: false });
    const [lastUpdatedExtensions, setLastUpdateExtensions] = useState<MigrationStatus>({ lastUpdatedId: null, updated: 0, aggregateUpdated: 0, complete: false });
    const [batchSize, setBatchSize] = useState(500);
    const [runningMigration, setRunningMigration] = useState<MigrationName | null>(null);
    const [migrationMessage, setMigrationMessage] = useState('');
    const stopRequested = useRef(false);
    const currentRequest = useRef<{ abort: () => void } | null>(null);
    const delayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const finishDelay = useRef<(() => void) | null>(null);

    const waitBetweenBatches = () => new Promise<void>((resolve) => {
        finishDelay.current = resolve;
        delayTimer.current = setTimeout(() => {
            delayTimer.current = null;
            finishDelay.current = null;
            resolve();
        }, MIGRATION_BATCH_DELAY_MS);
    });

    const runMigration = async (migration: MigrationName) => {
        setRunningMigration(migration);
        setMigrationMessage('');
        stopRequested.current = false;

        try {
            let lastId = migration === 'earnings' ? lastUpdatedEarnings.lastUpdatedId : lastUpdatedExtensions.lastUpdatedId;
            let complete = false;

            while (!complete && !stopRequested.current) {
                const request = migration === 'earnings'
                    ? updateEarnings({ batchSize, lastId })
                    : updateExtendStayEarnings({ batchSize, lastId });
                currentRequest.current = request;
                const data: any = await request.unwrap();
                const updatedStatus = {
                    lastUpdatedId: data.lastUpdatedId,
                    updated: data.updated,
                    complete: data.complete,
                };

                if (migration === 'earnings') {
                    setLastUpdateEarnings((previous) => ({
                        ...updatedStatus,
                        aggregateUpdated: previous.aggregateUpdated + data.updated,
                    }));
                } else {
                    setLastUpdateExtensions((previous) => ({
                        ...updatedStatus,
                        aggregateUpdated: previous.aggregateUpdated + data.updated,
                    }));
                }

                lastId = data.lastUpdatedId;
                complete = data.complete;

                if (!complete && !stopRequested.current) {
                    await waitBetweenBatches();
                }
            }

            setMigrationMessage(stopRequested.current ? 'Migration stopped.' : 'Migration complete.');
        } catch (error) {
            if (stopRequested.current) {
                setMigrationMessage('Migration stopped.');
            } else {
                console.error('Migration failed:', error);
                setMigrationMessage('Migration failed.');
            }
        } finally {
            currentRequest.current = null;
            setRunningMigration(null);
        }
    };

    const stopMigration = () => {
        stopRequested.current = true;
        currentRequest.current?.abort();
        if (delayTimer.current) {
            clearTimeout(delayTimer.current);
            delayTimer.current = null;
            finishDelay.current?.();
            finishDelay.current = null;
        }
    };

    return (
        <Container>
            <Row>
                <Col>
                    <h1>Migrations</h1>
                    <hr />
                    <p>Choose a migration to run:</p>
                    <div className="mb-3">
                        <strong>Last run</strong>
                        <div>Earnings: updated {lastUpdatedEarnings.aggregateUpdated}, last ID: {lastUpdatedEarnings.lastUpdatedId || 'none'}, complete: {lastUpdatedEarnings.complete ? 'Yes' : 'No'}</div>
                        <div>Extend stay earnings: updated {lastUpdatedExtensions.aggregateUpdated}, last ID: {lastUpdatedExtensions.lastUpdatedId || 'none'}, complete: {lastUpdatedExtensions.complete ? 'Yes' : 'No'}</div>
                        {migrationMessage && <div>{migrationMessage}</div>}
                    </div>
                    <Form.Group className="mb-3" controlId="migrationBatchSize">
                        <Form.Label>Batch size</Form.Label>
                        <Form.Control
                            type="number"
                            min={1}
                            value={batchSize}
                            disabled={runningMigration !== null}
                            onChange={(event) => setBatchSize(Number(event.target.value))}
                        />
                    </Form.Group>
                    <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                disabled={runningMigration !== null || isUpdatingEarnings || isUpdatingExtendStayEarnings}
                                onClick={() => runMigration('earnings')}
                            >
                                {isUpdatingEarnings ? 'Updating Earnings...' : 'Update Earnings'}
                            </Button>
                            <Button
                                variant="primary"
                                disabled={runningMigration !== null || isUpdatingEarnings || isUpdatingExtendStayEarnings}
                                onClick={() => runMigration('extendStay')}
                            >
                                {isUpdatingExtendStayEarnings ? 'Updating Extend Stay Earnings...' : 'Update Extend Stay Earnings'}
                            </Button>
                            <Button variant="outline-danger" disabled={runningMigration === null} onClick={stopMigration}>
                                Stop
                            </Button>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}